import React, { useState } from 'react';
import styled from 'styled-components';
import { UnstyledButton } from '../Button';
import VipIcon from '../../Icons/VipIcons';
import { ConfirmModal } from '../Modal';

// ==================== STYLED ====================

const Wrapper = styled.aside`
  display: flex;
  flex-direction: column;
  min-width: 220px;
  max-width: 260px;
  width: 240px;
  border-right: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.bg1 || theme.colors.bg2};
  overflow: hidden;

  @media (max-width: 768px) {
    width: 100%;
    max-width: none;
    min-width: 0;
    border-right: none;
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  }
`;

const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0.5rem 0;
  overflow-y: auto;
  flex: 1;
`;

const Row = styled.li`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px 6px ${({ $depth }) => 12 + ($depth ?? 0) * 16}px;
  cursor: pointer;
  color: ${({ theme, $active }) => $active ? theme.colors.primary : theme.colors.text};
  background: ${({ theme, $active }) => $active ? (theme.colors.primaryBg || `${theme.colors.primary}18`) : 'transparent'};
  font-size: 0.875rem;
  font-weight: ${({ $active }) => $active ? 600 : 500};
  border-radius: 6px;
  margin: 0 6px;

  &:hover {
    background: ${({ theme, $active }) => $active ? (theme.colors.primaryBg || `${theme.colors.primary}18`) : (theme.colors.hoverBg || 'rgba(0,0,0,0.04)')};
  }

  .name {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .count {
    font-size: 0.75rem;
    color: ${({ theme }) => theme.colors.textMuted};
    padding-left: 4px;
  }

  .actions {
    display: none;
    gap: 2px;
  }

  &:hover .actions {
    display: inline-flex;
  }
`;

const IconBtn = styled(UnstyledButton)`
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.textMuted};
  padding: 2px 4px;
  border-radius: 4px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: ${({ theme }) => theme.colors.bg2};
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const Twisty = styled(UnstyledButton)`
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.textMuted};
  padding: 0;
  width: 14px;
  height: 14px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transform: rotate(${({ $open }) => $open ? '90deg' : '0deg'});
  transition: transform 0.12s ease;
  flex-shrink: 0;
`;

const TwistySpacer = styled.span`
  display: inline-block;
  width: 14px;
  flex-shrink: 0;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  margin: 6px 12px;
`;

const AddRow = styled(UnstyledButton)`
  display: flex;
  align-items: center;
  gap: 8px;
  width: calc(100% - 12px);
  margin: 4px 6px 8px;
  padding: 6px 12px;
  border: 1px dashed ${({ theme }) => theme.colors.border};
  border-radius: 6px;
  background: transparent;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const InlineInput = styled.input`
  flex: 1;
  min-width: 0;
  padding: 2px 6px;
  border: 1px solid ${({ theme }) => theme.colors.primary};
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.bg2};
  color: ${({ theme }) => theme.colors.text};
  font-size: 0.8125rem;
  outline: none;
`;

// ==================== SELECTIONS ====================

export const VIEW_ALL = 'all';
export const VIEW_FAVORITES = 'favorites';
export const VIEW_FOLDER = 'folder';

/**
 * @typedef {Object} FolderSelection
 * @property {'all'|'favorites'|'folder'} view
 * @property {string|null} folderId
 */

// ==================== HELPERS ====================

const FolderRow = ({
  node,
  depth,
  selection,
  onSelect,
  onStartRename,
  onDelete,
  onAddChild,
  expandedIds,
  toggleExpanded,
  editingId,
  editingValue,
  onEditingChange,
  onCommitRename,
  onCancelRename,
}) => {
  const hasChildren = Array.isArray(node.children) && node.children.length > 0;
  const isOpen = expandedIds.has(node.id);
  const isActive = selection.view === VIEW_FOLDER && selection.folderId === node.id;
  const isEditing = editingId === node.id;

  return (
    <>
      <Row
        $active={isActive}
        $depth={depth}
        onClick={() => !isEditing && onSelect({ view: VIEW_FOLDER, folderId: node.id })}
      >
        {hasChildren ? (
          <Twisty
            $open={isOpen}
            onClick={(e) => { e.stopPropagation(); toggleExpanded(node.id); }}
            aria-label={isOpen ? 'Collapse' : 'Expand'}
          >
            <VipIcon name="ChevronRight" size={12} />
          </Twisty>
        ) : (
          <TwistySpacer />
        )}
        <VipIcon name="Gallery" size={16} />
        {isEditing ? (
          <InlineInput
            autoFocus
            value={editingValue}
            onChange={(e) => onEditingChange(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              if (e.key === 'Enter') onCommitRename();
              if (e.key === 'Escape') onCancelRename();
            }}
            onBlur={onCommitRename}
          />
        ) : (
          <>
            <span className="name" title={node.name}>{node.name}</span>
            {node.mediaCount > 0 && <span className="count">{node.mediaCount}</span>}
            <span className="actions" onClick={(e) => e.stopPropagation()}>
              <IconBtn title="Add subfolder" onClick={() => onAddChild(node.id)}>
                <VipIcon name="Plus" size={12} />
              </IconBtn>
              <IconBtn title="Rename" onClick={() => onStartRename(node)}>
                <VipIcon name="Draft" size={12} />
              </IconBtn>
              <IconBtn title="Delete folder" onClick={() => onDelete(node)}>
                <VipIcon name="Trash" size={12} />
              </IconBtn>
            </span>
          </>
        )}
      </Row>
      {isOpen && hasChildren && node.children.map((child) => (
        <FolderRow
          key={child.id}
          node={child}
          depth={depth + 1}
          selection={selection}
          onSelect={onSelect}
          onStartRename={onStartRename}
          onDelete={onDelete}
          onAddChild={onAddChild}
          expandedIds={expandedIds}
          toggleExpanded={toggleExpanded}
          editingId={editingId}
          editingValue={editingValue}
          onEditingChange={onEditingChange}
          onCommitRename={onCommitRename}
          onCancelRename={onCancelRename}
        />
      ))}
    </>
  );
};

// ==================== MAIN ====================

/**
 * FolderTree sidebar for the Media Library.
 *
 * Props:
 *   folders            — tree of MediaFolderDTO returned by GET /api/media/folders?view=tree
 *   selection          — { view: 'all' | 'favorites' | 'folder', folderId: string | null }
 *   onSelectionChange  — called with a new selection
 *   allowManagement    — if true, shows +/rename/delete UI (default true)
 *   onCreate           — async ({ name, parentId }) => folder
 *   onRename           — async ({ id, name }) => folder
 *   onDelete           — async (folder) => void  (the component asks for confirmation first)
 */
const FolderTree = ({
  folders = [],
  selection,
  onSelectionChange,
  allowManagement = true,
  onCreate,
  onRename,
  onDelete,
}) => {
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const [editingId, setEditingId] = useState(null);
  const [editingValue, setEditingValue] = useState('');
  const [pendingParentId, setPendingParentId] = useState(null); // parent for pending new folder
  const [folderToDelete, setFolderToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const toggleExpanded = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const startRename = (folder) => {
    setEditingId(folder.id);
    setEditingValue(folder.name);
  };

  const commitRename = async () => {
    if (!editingId) return;
    const value = editingValue.trim();
    const wasNew = editingId === '__new__';
    setEditingId(null);
    setEditingValue('');
    if (!value) { setPendingParentId(null); return; }
    if (wasNew) {
      try {
        const created = await onCreate?.({ name: value, parentId: pendingParentId });
        if (created && pendingParentId) {
          setExpandedIds((prev) => new Set(prev).add(pendingParentId));
        }
      } finally {
        setPendingParentId(null);
      }
      return;
    }
    await onRename?.({ id: editingId, name: value });
  };

  const cancelRename = () => {
    setEditingId(null);
    setEditingValue('');
    setPendingParentId(null);
  };

  const startNewFolder = (parentId = null) => {
    setPendingParentId(parentId);
    setEditingId('__new__');
    setEditingValue('New folder');
    if (parentId) setExpandedIds((prev) => new Set(prev).add(parentId));
  };

  const handleDelete = (folder) => {
    if (!onDelete) return;
    setFolderToDelete(folder);
  };

  const confirmDelete = async () => {
    if (!folderToDelete) return;
    setDeleting(true);
    try {
      await onDelete(folderToDelete);
    } finally {
      setDeleting(false);
      setFolderToDelete(null);
    }
  };

  const isNewAtRoot = editingId === '__new__' && pendingParentId === null;

  return (
    <Wrapper>
      <List>
        <Row
          $active={selection?.view === VIEW_ALL}
          $depth={0}
          onClick={() => onSelectionChange?.({ view: VIEW_ALL, folderId: null })}
        >
          <TwistySpacer />
          <VipIcon name="Gallery" size={16} />
          <span className="name">All media</span>
        </Row>
        <Row
          $active={selection?.view === VIEW_FAVORITES}
          $depth={0}
          onClick={() => onSelectionChange?.({ view: VIEW_FAVORITES, folderId: null })}
        >
          <TwistySpacer />
          <VipIcon name="StarIcon" size={16} />
          <span className="name">Favorites</span>
        </Row>

        <Divider />

        {folders.map((node) => (
          <FolderRow
            key={node.id}
            node={node}
            depth={0}
            selection={selection}
            onSelect={onSelectionChange}
            onStartRename={startRename}
            onDelete={handleDelete}
            onAddChild={(parentId) => startNewFolder(parentId)}
            expandedIds={expandedIds}
            toggleExpanded={toggleExpanded}
            editingId={editingId}
            editingValue={editingValue}
            onEditingChange={setEditingValue}
            onCommitRename={commitRename}
            onCancelRename={cancelRename}
          />
        ))}

        {isNewAtRoot && (
          <Row $depth={0}>
            <TwistySpacer />
            <VipIcon name="Gallery" size={16} />
            <InlineInput
              autoFocus
              value={editingValue}
              onChange={(e) => setEditingValue(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => {
                if (e.key === 'Enter') commitRename();
                if (e.key === 'Escape') cancelRename();
              }}
              onBlur={commitRename}
            />
          </Row>
        )}
      </List>

      {allowManagement && (
        <AddRow onClick={() => startNewFolder(null)}>
          <VipIcon name="Plus" size={14} />
          <span>New folder</span>
        </AddRow>
      )}
      <ConfirmModal
        isOpen={!!folderToDelete}
        onClose={() => setFolderToDelete(null)}
        onConfirm={confirmDelete}
        title="Delete folder"
        message={`Delete folder "${folderToDelete?.name}"? Media inside will not be deleted; it will return to All Media.`}
        confirmText="Delete"
        danger
        loading={deleting}
      />
    </Wrapper>
  );
};

export default FolderTree;
